import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getIdToken } from '../../services/authService';
import { renderMarkdown } from './markdown';
import { useImeSafeEnter } from '../../hooks/useImeSafeEnter';

/**
 * Authenticated analytics-chat embed — the third chat integration pattern
 * (alongside full-page web chat and the anonymous iframe).
 *
 * Renders the analysis support bot natively inside the Admin UI and talks to
 * the iframe Lambda's `/api/iframe/chat/stream-auth` route, sending the admin's
 * Cognito ID token as an `Authorization` header — the same identity the rest of
 * the admin console uses. The token never crosses an origin boundary into an
 * iframe; it's a normal cross-origin fetch with CORS.
 *
 * SSE parsing mirrors chat-ui/src/services/iframeApi.ts (the anonymous iframe
 * client) but adds the auth header and drops the integrationId-only model.
 */

interface AnalyticsChatProps {
  origin: string;
  integrationId: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** true while the assistant turn is still streaming */
  streaming?: boolean;
  /** true if this assistant turn ended in an error (content is the message) */
  error?: boolean;
}

function getOrCreateSessionId(integrationId: string): string {
  const key = `analyticsChatSession:${integrationId}`;
  let sid = sessionStorage.getItem(key);
  if (!sid) {
    sid =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(key, sid);
  }
  return sid;
}

const AnalyticsChat: React.FC<AnalyticsChatProps> = ({ origin, integrationId }) => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const sessionIdRef = useRef<string>(getOrCreateSessionId(integrationId));
  const scrollRef = useRef<HTMLDivElement>(null);
  const baseUrl = `${origin.replace(/\/$/, '')}/api/iframe`;

  // Auto-scroll to the latest message
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Restore prior history for this admin's session on mount (best-effort).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const idToken = await getIdToken();
        if (!idToken) return;
        const url =
          `${baseUrl}/sessions/${encodeURIComponent(sessionIdRef.current)}/messages-auth` +
          `?integrationId=${encodeURIComponent(integrationId)}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !Array.isArray(data?.messages)) return;
        const restored: ChatMessage[] = data.messages
          .filter((m: any) => m.role === 'user' || m.role === 'assistant')
          .map((m: any) => ({ role: m.role, content: m.content }));
        if (restored.length > 0) setMessages(restored);
      } catch {
        /* fresh session — ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl, integrationId]);

  const errorMessageFor = useCallback(
    (code: string): string => {
      switch (code) {
        case 'unauthorized':
          return t('analytics.chat.errorUnauthorized');
        case 'analytics_disabled':
          return t('analytics.chat.errorDisabled');
        default:
          return t('analytics.chat.errorGeneric');
      }
    },
    [t]
  );

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;

    setInput('');
    setBusy(true);
    // Append the user turn + a placeholder streaming assistant turn.
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '', streaming: true },
    ]);

    const updateAssistant = (patch: Partial<ChatMessage>) =>
      setMessages((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === 'assistant') {
            next[i] = { ...next[i], ...patch };
            break;
          }
        }
        return next;
      });

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        updateAssistant({ content: errorMessageFor('unauthorized'), streaming: false, error: true });
        return;
      }

      const res = await fetch(`${baseUrl}/chat/stream-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          integrationId,
          sessionId: sessionIdRef.current,
          message: text,
          lang: i18n.language?.startsWith('ja') ? 'ja' : 'en',
        }),
      });

      if (!res.ok || !res.body) {
        updateAssistant({ content: errorMessageFor('generic'), streaming: false, error: true });
        return;
      }

      // ---- SSE parse (mirrors chat-ui iframeApi.ts) ----
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let dataLines: string[] = [];
      let live = '';
      let terminated = false;

      const dispatch = (raw: string): boolean => {
        let evt: Record<string, unknown>;
        try {
          evt = JSON.parse(raw);
        } catch {
          return false;
        }
        switch (evt.type) {
          case 'chunk':
          case 'token':
            if (typeof evt.content === 'string') {
              live += evt.content;
              updateAssistant({ content: live, streaming: true });
            }
            return false;
          case 'done':
            updateAssistant({
              content: (evt.fullText as string) || live || '',
              streaming: false,
            });
            return true;
          case 'error':
            updateAssistant({
              content: errorMessageFor((evt.message as string) || 'generic'),
              streaming: false,
              error: true,
            });
            return true;
          default:
            return false; // metadata / progress / tool_* — no UI change
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split(/\r?\n/);
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.trim()) {
            if (dataLines.length > 0) {
              if (dispatch(dataLines.join('\n'))) terminated = true;
              dataLines = [];
            }
            continue;
          }
          if (line.startsWith('data:')) {
            dataLines.push(line.startsWith('data: ') ? line.slice(6) : line.slice(5));
          }
        }
      }
      if (dataLines.length > 0 && dispatch(dataLines.join('\n'))) terminated = true;

      if (!terminated) {
        updateAssistant({
          content: live || errorMessageFor('generic'),
          streaming: false,
          error: !live,
        });
      }
    } catch {
      updateAssistant({ content: errorMessageFor('generic'), streaming: false, error: true });
    } finally {
      setBusy(false);
    }
  }, [input, busy, baseUrl, integrationId, i18n.language, errorMessageFor]);

  const imeEnter = useImeSafeEnter<HTMLTextAreaElement>((e) => {
    if (e.shiftKey) return; // Shift+Enter = newline
    e.preventDefault();
    send();
  });

  return (
    <div className="analytics-chat">
      <div className="analytics-chat-messages" ref={scrollRef}>
        {messages.length === 0 && (
          <p className="analytics-chat-empty">{t('analytics.chat.emptyState')}</p>
        )}
        {messages.map((m, idx) => (
          <div key={idx} className={`analytics-chat-bubble analytics-chat-bubble--${m.role}`}>
            {m.role === 'assistant' ? (
              m.streaming && !m.content ? (
                <span className="analytics-chat-thinking">{t('analytics.chat.thinking')}</span>
              ) : (
                <div className={`analytics-chat-md${m.error ? ' analytics-chat-md--error' : ''}`}>
                  {renderMarkdown(m.content)}
                </div>
              )
            ) : (
              <span>{m.content}</span>
            )}
          </div>
        ))}
      </div>
      <div className="analytics-chat-input-row">
        <textarea
          className="analytics-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          {...imeEnter.props}
          placeholder={t('analytics.chat.placeholder')}
          rows={1}
          disabled={busy}
        />
        <button
          className="analytics-chat-send"
          onClick={send}
          disabled={busy || !input.trim()}
        >
          {t('analytics.chat.send')}
        </button>
      </div>
    </div>
  );
};

export default AnalyticsChat;
